import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterChipAutosizeHelper } from '@zaoblako/web/shared/helpers/filter-chip-autosize.helper';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { NestedPropertyPipe } from '@zaoblako/web/shared/pipes/nested-property.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { FindBySelectedItemPipe } from '@zaoblako/web/shared/pipes/find-by-selected-item.pipe';
import { FRM_FILTERS_UI_V2_PANEL_CLASS, type FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';

import { isNil } from 'lodash';
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent, DropdownModule } from 'primeng/dropdown';
import { FloatLabelModule } from 'primeng/floatlabel';
import type { ScrollerOptions } from 'primeng/api';

@Component({
  standalone: true,
  selector: 'crm-single-select',
  templateUrl: './single-select.component.html',
  styleUrls: ['./single-select.component.scss'],
  host: {
    '[class.frm-filters-ui-v2]': 'filtersUiVersion === "v2"'
  },
  imports: [
    CommonModule,
    DropdownModule,
    OrderByPipe,
    FormsModule,
    NestedPropertyPipe,
    SafeHtmlPipe,
    FloatLabelModule,
    FindBySelectedItemPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SingleSelectComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('select') public readonly dropdown: Dropdown;
  public isNotIdArray = false;

  @Input() public set options(array: any[]) {
    this.buildOptions(array);
  }

  @Input() public set selectedItem(selectedItem: any) {
    this._inputSelectedItem = selectedItem;
    this.buildSelectedItem(this._inputSelectedItem);
  }

  public _options: any[] = [];
  public _selectedItem: any = null;
  private _inputSelectedItem: any;

  @Input() public class: string;
  @Input() public disabled = false;
  @Input() public editValue = 'name';
  @Input() public editable = false;
  @Input() public emptyFilterMessage = 'Нет результатов';
  @Input() public emptyMessage = 'Список пуст';
  @Input() public errorMessage: string;
  @Input() public filter = true;
  @Input() public filterBy = 'name';
  @Input() public autoFocusFilter = false;
  @Input() public inputId = Math.random().toString();
  @Input() public itemDescriptionField: string;
  @Input() public itemSubDescriptionField: string;
  @Input() public label: string;
  @Input() public optionLabel = 'name';
  @Input() public optionValue = 'id';
  @Input() public placeholder = '\u00A0';
  @Input() public showClear = false;
  @Input() public sort = this.optionLabel;
  @Input() public styleClass: any;
  @Input() public scrollHeight = '300px';
  @Input() public appendTo: any;
  @Input() public isFilterSelectedItem = false;
  @Input() public checkmark = true;
  @Input() public group = false;
  @Input() public loading = false;
  @Input() public panelStyle: any;
  @Input() public panelStyleClass: string;

  @Output() public changeValue = new EventEmitter<any>();
  @Output() public selectedItemChange = new EventEmitter<any>();
  @Output() public onShow = new EventEmitter<any>();
  @Output() public onHide = new EventEmitter<any>();
  @Output() public onFilter = new EventEmitter<DropdownFilterEvent>();

  @Input() public virtualScroll = false;
  @Input() public virtualScrollItemSize: number;
  /** Фиксированная высота списка при virtualScroll (без autoSize PrimeNG). */
  @Input() public virtualScrollOptions: ScrollerOptions = { autoSize: false };

  @ContentChild('groupTemplate', { static: false }) public readonly groupTemplate: TemplateRef<any>;
  @ContentChild('itemTemplate', { static: false }) public readonly itemTemplate: TemplateRef<any>;
  @ContentChild('selectedItemTemplate', { static: false }) public readonly selectedItemTemplate: TemplateRef<any>;
  @ContentChild('footerTemplate', { static: false }) public readonly footerTemplate: TemplateRef<any>;
  @ContentChild('emptyTemplate', { static: false }) public readonly emptyTemplate: TemplateRef<any>;

  @Input() public isTemplateCombined = false;
  @Input() public isError = false;
  @Input() public itemStyleClass: string;
  /** Оформление панели и списка как у фильтров FRM 2.0 (`_filters-ui-v2.scss`). */
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  public get mergedPanelStyleClass(): string {
    const base = this.panelStyleClass?.trim() ?? '';
    const v2 = this.filtersUiVersion === 'v2' ? FRM_FILTERS_UI_V2_PANEL_CLASS : '';
    return [base, v2].filter(Boolean).join(' ');
  }

  private chipAutosize: FilterChipAutosizeHelper | null = null;
  private viewInitialized = false;

  public constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  public ngAfterViewInit() {
    this.viewInitialized = true;
    this.setupChipAutosize();
    this.refreshChipAutosize();
  }

  public ngOnDestroy() {
    this.chipAutosize?.disconnect();
    this.chipAutosize = null;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.editable && this.dropdown?.editableInputViewChild) {
      this.cdRef.detectChanges();
      this.dropdown.editableInputViewChild.nativeElement.value = this.editValue;
    }
    if (this.viewInitialized && (changes.label || changes.filtersUiVersion)) {
      this.setupChipAutosize();
    }
    if (this.viewInitialized && (changes.selectedItem || changes.options)) {
      this.refreshChipAutosize();
    }
  }

  public onChangeValue(event: DropdownChangeEvent) {
    event.originalEvent.stopPropagation();
    this.errorMessage = null;
    this.class = null;
    if (isNil(event.value)) {
      this.changeValue.emit(null);
      this.selectedItemChange.emit(null);
      if (this.dropdown?.filterValue?.length) {
        this.dropdown.resetFilter();
        this.onFilter.emit({ filter: '', originalEvent: event.originalEvent });
      }
      this.refreshChipAutosize();
      return;
    }
    if (this.isNotIdArray) {
      this.changeValue.emit(event.value);
      this.selectedItemChange.emit(event.value);
    } else {
      const value = new FindBySelectedItemPipe().transform(event.value, this._options, this.optionValue, this.group);
      this.changeValue.emit(value);
      this.selectedItemChange.emit(value);
    }

    if (this.dropdown?.filterValue?.length) {
      this.dropdown.resetFilter();
      this.onFilter.emit({ filter: '', originalEvent: event.originalEvent });
    }
    this.refreshChipAutosize();
  }

  public onShowPanel() {
    this.onShow.emit();

    if (!this.autoFocusFilter) {
      return;
    }

    setTimeout(() => {
      const dropdownElement = (this.dropdown as any)?.el?.nativeElement as HTMLElement | undefined;
      const filterInput = dropdownElement?.querySelector('input.p-dropdown-filter') as HTMLInputElement | null;
      if (filterInput) {
        filterInput.focus();
        filterInput.select();
      }

      const panelElement = (this.dropdown as any)?.overlayViewChild?.nativeElement as HTMLElement | undefined;
      const itemsWrapper = panelElement?.querySelector('.p-dropdown-items-wrapper') as HTMLElement | null;
      if (itemsWrapper) {
        itemsWrapper.scrollTop = 0;
      }
    });
  }

  public onHidePanel() {
    if (this.dropdown?.filterValue?.length) {
      this.dropdown.resetFilter();
      this.onFilter.emit({ filter: '', originalEvent: new Event('filter') });
    }

    this.onHide.emit();
    this.refreshChipAutosize();
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.dropdown?.filterValue);
  }

  private shouldUseChipAutosize(): boolean {
    return this.filtersUiVersion === 'v2' && !!this.label;
  }

  private setupChipAutosize(): void {
    this.chipAutosize?.disconnect();
    this.chipAutosize = null;

    if (!this.shouldUseChipAutosize()) {
      return;
    }

    this.chipAutosize = new FilterChipAutosizeHelper(this.elementRef.nativeElement);
    this.chipAutosize.connect();
  }

  private refreshChipAutosize(): void {
    this.chipAutosize?.scheduleSync();
  }

  private buildOptions(options: any[]) {
    if (!Array.isArray(options)) {
      return;
    }

    if (typeof options[0] === 'string' || typeof options[0] === 'number') {
      this._options = options.map((item) => ({
        name: item,
        value: item
      }));
      this.isNotIdArray = true;
      this.optionValue = 'value';
    } else {
      this._options = options;
    }

    this.buildSelectedItem(this._inputSelectedItem);
  }

  private buildSelectedItem(selectedItem: any) {
    if ((typeof selectedItem === 'string' || typeof selectedItem === 'number') && this.isNotIdArray) {
      this._selectedItem = this._options.find((opt) => selectedItem === opt.value)?.value;
    }

    if ((typeof selectedItem === 'string' || typeof selectedItem === 'number') && !this.isNotIdArray) {
      if (this.group) {
        let _selectedItem: any;
        this._options.forEach((group) => {
          if (group.items.findIndex((opt: any) => selectedItem === opt[this.optionValue]) !== -1) {
            _selectedItem = group.items.find((opt: any) => selectedItem === opt[this.optionValue])[this.optionValue];
          }
        });
        this._selectedItem = _selectedItem;
      } else {
        this._selectedItem = this._options.find((opt) => selectedItem === opt[this.optionValue])?.[this.optionValue];
      }
    }

    if (typeof selectedItem === 'object' && selectedItem) {
      if (this.group) {
        let _selectedItem: any;
        this._options.forEach((group) => {
          if (group.items.findIndex((opt: any) => selectedItem[this.optionValue] === opt[this.optionValue]) !== -1) {
            _selectedItem = group.items.find((opt: any) => selectedItem[this.optionValue] === opt[this.optionValue])[
              this.optionValue
            ];
          }
        });
        this._selectedItem = _selectedItem;
      } else {
        this._selectedItem = this._options.find((opt) => selectedItem[this.optionValue] === opt[this.optionValue])?.[
          this.optionValue
        ];
      }
    } else if (typeof selectedItem === 'object' && !selectedItem) {
      this._selectedItem = selectedItem;
    }

    if (!this._options?.length) {
      this._selectedItem = selectedItem?.[this.optionValue] || selectedItem;
    }

    this.sortedOptions();
    this.refreshChipAutosize();
    this.cdRef.markForCheck();
  }

  private sortedOptions() {
    if (this.isFilterSelectedItem && this._options?.length && this._selectedItem) {
      const key = this.isNotIdArray ? 'value' : this.optionValue;
      this._options = this._options.map((option) => ({
        ...option,
        isSelected: option?.[key] === this._selectedItem?.[key]
      }));
    }
  }
}
